// axl2/FrontPage/FPproducts.jsx
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useViewport } from '../../context/ViewportContext';

// Import icons
import { 
  Package,
  ChevronRight,
  ArrowRight,
  Eye,
  Box,
  AlertTriangle,
  FileText,
  X
} from 'lucide-react';

// Import from productGroupSlice
import { 
  fetchProductGroups,
  selectProductGroups,
  selectProductGroupsLoading,
  selectProductGroupsError,
  selectImagePathGroup,
} from '../../store/productGroupSlice';


import { getIcon } from "../Service/srv_iconMap";

// Product Card Component
const ProductCard = ({ product, group, imagePathGroup }) => {
  const { isMobile } = useViewport();
  const [imgError, setImgError] = useState(false);
  //console.log(product)
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm hover:shadow-md transition-all hover:border-blue-500/30 flex flex-col h-full group">
       {/* Group Title - Above image */}
<div className="mb-2 text-center group/title relative"
title={product.title.length > 20 ? product.title : ''}>
  <span 
    className="text-xs sm:text-sm font-semibold text-[#5c67aa] uppercase tracking-wider block truncate px-2"
    ref={(el) => {
      if (el && el.scrollWidth > el.clientWidth) {
        el.dataset.truncated = 'true';
      }
    }}
  >
    {product.title}
  </span>
  
  {/* Show tooltip only if text is actually truncated */}
  {product.title.length > 20 && (
    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-1.5 bg-gray-800 text-white text-xs rounded opacity-0 group-hover/title:opacity-100 transition-opacity whitespace-nowrap z-10 pointer-events-none shadow-lg">
      {product.title}
    </div>
  )}
</div>
     
      {/* Product Image with Link */}
      <Link 
        to={`/product/model/${product.id}`}
        className="rounded-lg overflow-hidden mb-3 flex items-center justify-center h-32 transition-transform group-hover:scale-[1.02] bg-gray-50 block relative"
      >  
      
      
      
        {!imgError ? (
          <img 
            src={`${imagePathGroup}${product.image}`}
            alt={product.name}
            className="object-contain w-full h-full p-2"
            style={{ transform: `scale(${product.scaleImage || 1})` }}
            onError={() => setImgError(true)}
          />
        ) : (
          <div className={`w-full h-full flex items-center justify-center ${product.color || 'bg-gradient-to-br from-gray-100 to-gray-200'} rounded-lg`}>
            <span className="text-gray-400 font-bold text-xl">{product.name}</span>
          </div>
        )}
        
        {/* Quick view badge on hover */}
        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center rounded-lg">
          <span className="text-white text-sm font-medium">View Details</span>
        </div>
      </Link>

      <div className="flex-grow">
        <div className="flex justify-between items-start mb-2">
          {/* Product Name as Link */}
          <Link 
            to={`/product/model/${product.id}`}
            className="font-bold text-gray-800 hover:text-blue-600 transition-colors"
          >
            {product.name}
          </Link>
          
          {/* PDF Download Link - underlined with icon */}
          <a 
            href={product.pdfUrl || '#'}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-blue-600 hover:text-blue-800 underline underline-offset-2 flex items-center gap-1 transition-colors"
            onClick={(e) => {
              if (!product.pdfUrl) {
                e.preventDefault();
                console.log('PDF not available');
              }
            }}
          >
            <FileText className="w-3 h-3" />
            <span>Spec PDF</span>
          </a>
        </div>
        
        {/* Size with icon */}
        <div className="flex items-center gap-2 text-gray-500 mb-3">
          <Box className="w-4 h-4" />
          <span className="text-sm font-medium">{product.size}</span>
        </div>
      </div>


{product.technicalDetails && (
  <div className="space-y-1.5 mb-4 text-xs">
    {product.technicalDetails.map((spec, idx) => (
      <div key={idx} className="flex justify-between items-center border-b border-gray-100 pb-1 last:border-0">
        <span className="text-gray-500">{spec.label}:</span>
        <span className="font-medium text-gray-800">{spec.value}</span>
      </div>
    ))}
  </div>
)}


      {/* Action button bg-blue-600 hover:bg-blue-700 text-white */}
{/*       <Link
        to={`/product/model/${product.id}`}
        className="flex-1 py-2 text-sm font-medium text-white bg-[#5c67aa] hover:bg-[#4f5aa3] rounded-lg transition-colors flex items-center justify-center"
      >
        <span>View details</span>
      </Link>
      <br /> */}
      <Link
        to={`/product/model/${product.id}`}
        className="flex-1 py-2 text-sm font-medium text-[#5c67aa] border border-[#5c67aa] hover:bg-[#5c67aa] hover:text-white rounded-lg transition-all duration-200 flex items-center justify-center group/btn"
      >
        <span>to details & order</span>
        <ChevronRight className="w-4 h-4 ml-1 group-hover/btn:translate-x-0.5 transition-transform" />
      </Link>

    </div>
  );
};

// Main Component
const FPproducts = () => {
  const { isMobile } = useViewport();
  const dispatch = useDispatch();
  const [selectedTag, setSelectedTag] = useState('all');
  const [headerHeight, setHeaderHeight] = useState(80);
  
  const productsSectionRef = useRef(null);
  const tagContainerRef = useRef(null);

    // Get header height dynamically
    useEffect(() => {
      const updateHeaderHeight = () => {
        const header = document.querySelector('header');
        if (header) {
          setHeaderHeight(header.offsetHeight);
        }
      };
      
      updateHeaderHeight();
      window.addEventListener('resize', updateHeaderHeight);
      return () => window.removeEventListener('resize', updateHeaderHeight);
    }, []);

    // Scroll to section function
    const scrollToSection = useCallback((tagId) => {
      setTimeout(() => {
        let targetElement;
        
        if (tagId === 'all') {
          targetElement = tagContainerRef.current;
        } else {
          targetElement = productsSectionRef.current;
        }
        
        if (targetElement) {
          const elementPosition = targetElement.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerHeight - 20; // Extra 20px padding
          
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }, 100);
    }, [headerHeight]);
    
    // Handle tag click
    const handleTagClick = useCallback((tagId) => {
      setSelectedTag(tagId);
      scrollToSection(tagId);
    }, [scrollToSection]);
        
    
  // Get data from Redux
  const productGroups = useSelector(selectProductGroups);
  const loading = useSelector(selectProductGroupsLoading);
  const error = useSelector(selectProductGroupsError);
  const imagePathGroup = useSelector(selectImagePathGroup);
  
  // Calculate all tags - simple and clean
  const allTags = [
    { 
      id: 'all', 
      name: 'All Products',
      count: productGroups.reduce((acc, group) => acc + group.products.length, 0)
    },
    ...productGroups.map(group => ({
      id: group.id,
      name: group.caption,
      count: group.products.length,
      specifications: group.specifications
    }))
  ];

  // Get filtered products
  const getFilteredProducts = () => {
    if (selectedTag === 'all') {
      return productGroups.flatMap(group => 
        group.products.map(product => ({
          ...product,
          groupCaption: group.caption,
          groupSpecifications: group.specifications,
          groupColor: group.color,
          groupHeaderGradient: group.headerGradient
        }))
      );
    } else {
      const group = productGroups.find(g => g.id === selectedTag);
      return group?.products.map(product => ({
        ...product,
        groupCaption: group.caption,
        groupSpecifications: group.specifications,
        groupColor: group.color,
        groupHeaderGradient: group.headerGradient
      })) || [];
    }
  };

  const filteredProducts = getFilteredProducts();
  const selectedGroupData = productGroups.find(g => g.id === selectedTag);

  // Fetch groups on mount
  useEffect(() => {
    if (productGroups.length === 0) {
      dispatch(fetchProductGroups());
    }
  }, [dispatch, productGroups.length]);

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading products...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center max-w-md">
          <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-800 mb-2">Error Loading Products</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <button 
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100">
      <main className="container mx-auto px-4 py-6 md:py-8">
        
        {/* Simple Header - Uncommented as in old style */}
{/*         <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-light text-gray-900 mb-2">
            Precision OCXOs
          </h1>
          <p className="text-gray-500">
            {filteredProducts.length} products available
          </p>
        </div> */}

        {/* Simple Tags Navigation - full width on mobile */}


  {/* Tags Container with Ref */}
  <div ref={tagContainerRef} className="mb-8">
    <div className={`flex flex-wrap gap-3 ${isMobile ? 'flex-col' : 'justify-center'}`}>
      {allTags.map(tag => (
        <button
          key={tag.id}
          //onClick={() => setSelectedTag(tag.id)}
          onClick={() => handleTagClick(tag.id)}
          className={`group relative px-5 py-2.5 rounded-xl transition-all ${
            isMobile ? 'w-full text-left' : ''
          } ${
            selectedTag === tag.id
              ? 'bg-violet-600 text-white shadow-md shadow-violet-200 border border-violet-400 scale-105 dark:bg-violet-500 dark:text-white dark:shadow-violet-900/50 dark:border-violet-400'
              : 'bg-violet-50/70 text-violet-700 hover:text-violet-800 hover:bg-violet-100 border border-violet-200 hover:border-violet-400 shadow-sm hover:shadow dark:bg-violet-950/30 dark:text-violet-300 dark:hover:text-violet-200 dark:hover:bg-violet-900/50 dark:border-violet-800 dark:hover:border-violet-600'
          }`}
        >
          <span className="font-medium">{tag.name}</span>

          {tag.name != "All Products" && (
            <span className={`ml-2 text-xs px-2 py-0.5 rounded-lg ${
              selectedTag === tag.id
                ? 'bg-violet-500 text-white dark:bg-violet-400 dark:text-violet-950'
                : 'bg-violet-100 text-violet-700 border border-violet-200 dark:bg-violet-900 dark:text-violet-300 dark:border-violet-800'
            }`}>
              {tag.count}
            </span>
          )}

          {!isMobile && tag.name.length > 30 && (
            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-1 bg-violet-700 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10 shadow-lg border border-violet-500 dark:bg-violet-600 dark:text-white dark:border-violet-400">
              {tag.name}
            </div>
          )}
        </button>
      ))}
      
      {selectedTag !== 'all' && (
        <button
          //onClick={() => setSelectedTag('all')}
          onClick={() => handleTagClick('all')}
          className={`px-4 py-2.5 text-violet-600 hover:text-violet-800 dark:text-violet-400 dark:hover:text-violet-200 flex items-center gap-1 transition-colors ${
            isMobile ? 'w-full justify-center mt-3 border-t border-violet-200 dark:border-violet-800 pt-4' : ''
          }`}
          title="Clear filter"
        >
          <X className="w-4 h-4" />
          <span className="text-sm font-medium">Clear all filters</span>
        </button>
      )}
    </div>
  </div>





        {/* Selected Group Details - styled like ProductGroupCard */}
        {/* Products Section with Ref */}
        <div ref={productsSectionRef}>
          {selectedTag !== 'all' && selectedGroupData && (
            <div className={`${selectedGroupData.color || 'bg-white'} border rounded-2xl shadow-sm overflow-hidden transition-all duration-300 ${isMobile ? 'mb-3' : 'mb-8'} hover:shadow-md`}>
              
              {/* Header with gradient - exactly like ProductGroupCard */}
              <div 
                className={`bg-gradient-to-r ${selectedGroupData.headerGradient || 'from-blue-600 to-indigo-600'} text-white ${isMobile ? 'p-2' : 'p-8'} relative transition-all duration-300`}
              >
                <div className="flex justify-between items-start gap-4">
                  <div className="flex-1">
                    <div className={`flex items-center gap-2 md:mb-2 ${isMobile && 'px-2'}`}>
                      <h2 className="text-xl md:text-3xl font-bold pr-2">{selectedGroupData.caption}</h2>
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-4 md:p-6">
                {/* Mobile Specifications */}
                {isMobile && (
                  <div className="space-y-1.5 mt-3 mb-4">
                    {selectedGroupData.specifications?.map((spec, index) => {
                      const IconComponent = getIcon(spec.icon);
                      return (
                        <div key={index} className="flex items-center gap-2.5 text-gray-700">
                          <IconComponent className="w-4 h-4 text-gray-600 flex-shrink-0" />
                          <div className="flex flex-col">
                            <span className="text-sm font-semibold text-gray-800">{spec.value}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )} 

                {/* Desktop Specifications - like old code */}
                {!isMobile && (
                  <div className="mb-6">
                    <div className="flex flex-wrap gap-3">
                      {selectedGroupData.specifications?.map((spec, index) => {
                        const IconComponent = getIcon(spec.icon);
                        return (
                          <div key={index} className="flex items-center gap-2 bg-white/70 backdrop-blur-sm rounded-xl p-3 border border-gray-200 hover:border-gray-300 transition-colors min-w-[200px]">
                            <IconComponent className="w-4 h-4 text-gray-600" />
                            <div className="flex flex-col">
                              <span className="text-sm font-medium text-gray-600">{spec.label}</span>
                              <span className="text-gray-800 font-semibold">{spec.value}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}


                {/* Products count badge - moved inside content area like old code */}
                <div className="pt-4 border-t border-gray-200">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold text-gray-700 flex items-center gap-2">
                      <Package className="w-5 h-5" />
                      Products
                    </h3>
                    <div className="text-sm text-gray-500">
                      {selectedGroupData.products.length} models available
                    </div>
                  </div>

                  {/* Products Grid */}
                  {filteredProducts.length > 0 ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                      {filteredProducts.map(product => {
                        // Find the original group for this product
                        const group = productGroups.find(g => 
                          g.products.some(p => p.id === product.id)
                        ) || { specifications: [] };
                        
                        return (
                          <ProductCard
                            key={product.id}
                            product={product}
                            group={group}
                            imagePathGroup={imagePathGroup}
                          />
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
                      <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                      <h3 className="text-xl font-medium text-gray-700 mb-2">No products found</h3>
                      <p className="text-gray-500">Try selecting a different category</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        
          {/* If 'all' is selected, show all products without group header */}
          {selectedTag === 'all' && (
            <div>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-700 flex items-center gap-2">
                  <Package className="w-5 h-5" />
                  All Products
                </h3>
                <div className="text-sm text-gray-500">
                  {filteredProducts.length} models available
                </div>
              </div>

              {/* Products Grid */}
              {filteredProducts.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {filteredProducts.map(product => {
                    // Find the original group for this product
                    const group = productGroups.find(g => 
                      g.products.some(p => p.id === product.id)
                    ) || { specifications: [] };
                    
                    return (
                      <ProductCard
                        key={product.id}
                        product={product}
                        group={group}
                        imagePathGroup={imagePathGroup}
                      />
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
                  <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-xl font-medium text-gray-700 mb-2">No products found</h3>
                  <p className="text-gray-500">Try selecting a different category</p>
                </div>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default FPproducts;
